import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TablerIconsModule } from 'angular-tabler-icons';
import { Tax } from '../../../services/apps/taxes/taxes.service';
import { computeInvoiceLine } from '../../../utils/invoice-tax-computation';

export interface InvoiceItemTaxContext {
  tenantTaxMode: string | null;
  applicableTaxes: Tax[];
  allTenantTaxes: Tax[];
}

export interface InvoiceItemDialogData {
  mode: 'create' | 'edit';
  item?: any;
  index?: number;
  invoiceTaxContext?: InvoiceItemTaxContext;
}

@Component({
  selector: 'app-invoice-item-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    TablerIconsModule
  ],
  templateUrl: './invoice-item-dialog.component.html',
  styleUrls: ['./invoice-item-dialog.component.scss']
})
export class InvoiceItemDialogComponent implements OnInit {
  itemForm!: FormGroup;
  loading = false;
  isEditMode = false;

  constructor(
    public dialogRef: MatDialogRef<InvoiceItemDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: InvoiceItemDialogData,
    private fb: FormBuilder
  ) {
    this.isEditMode = data.mode === 'edit';
  }

  ngOnInit(): void {
    this.initForm();
    
    if (this.isEditMode && this.data.item) {
      this.loadItemData();
    }
  }

  initForm(): void {
    this.itemForm = this.fb.group({
      description: ['', Validators.required],
      quantity: [1, [Validators.required, Validators.min(0.01)]],
      unitPrice: [0, [Validators.required, Validators.min(0)]],
      taxRate: [
        0,
        this.hasFreeTaxInput ? [Validators.required, Validators.min(0), Validators.max(100)] : []
      ],
      subtotal: [{ value: 0, disabled: true }],
      taxAmount: [{ value: 0, disabled: true }],
      total: [{ value: 0, disabled: true }],
      lineTaxes: [[] as unknown[]]
    });

    this.itemForm.get('quantity')?.valueChanges.subscribe(() => this.calculateTotals());
    this.itemForm.get('unitPrice')?.valueChanges.subscribe(() => this.calculateTotals());
    this.itemForm.get('taxRate')?.valueChanges.subscribe(() => this.calculateTotals());
  }

  /** Impuestos pre-filtrados por modo fiscal (se aplican automáticamente). */
  get useTenantTaxScheme(): boolean {
    return (this.data.invoiceTaxContext?.applicableTaxes ?? []).length > 0;
  }

  /** Catálogo completo del tenant (para display y cálculo cuando no hay modo fiscal activo). */
  get availableTaxes(): Tax[] {
    return this.data.invoiceTaxContext?.allTenantTaxes ?? [];
  }

  /** Catálogo disponible pero sin modo fiscal activo: se aplican todos del catálogo automáticamente. */
  get hasManualTaxSelector(): boolean {
    return !this.useTenantTaxScheme && this.availableTaxes.length > 0;
  }

  /** Sin catálogo: campo libre de porcentaje (fallback). */
  get hasFreeTaxInput(): boolean {
    return !this.useTenantTaxScheme && this.availableTaxes.length === 0;
  }

  get previewLineTaxes(): { taxName: string; ratePercent: number; baseAmount: number; taxAmount: number }[] {
    const v = this.itemForm?.get('lineTaxes')?.value;
    return Array.isArray(v) ? v : [];
  }

  loadItemData(): void {
    if (this.data.item) {
      this.itemForm.patchValue(this.data.item);
      this.calculateTotals();
    }
  }

  calculateTotals(): void {
    const quantity = Number(this.itemForm.get('quantity')?.value) || 0;
    const unitPriceFinal = Number(this.itemForm.get('unitPrice')?.value) || 0;
    const gross = quantity * unitPriceFinal;

    if (this.useTenantTaxScheme || this.hasManualTaxSelector) {
      const taxes = this.useTenantTaxScheme
        ? this.data.invoiceTaxContext!.applicableTaxes
        : this.availableTaxes;
      const computed = computeInvoiceLine(quantity, gross, taxes);
      const total = Math.round((computed.lineNetTotal + computed.lineTaxTotal) * 100) / 100;
      this.itemForm.get('subtotal')?.setValue(computed.lineNetTotal, { emitEvent: false });
      this.itemForm.get('taxAmount')?.setValue(computed.lineTaxTotal, { emitEvent: false });
      this.itemForm.get('total')?.setValue(total, { emitEvent: false });
      this.itemForm.get('lineTaxes')?.setValue(computed.lineTaxes, { emitEvent: false });
      return;
    }

    // Fallback: campo libre sin catálogo
    const taxRate = Number(this.itemForm.get('taxRate')?.value) || 0;
    const unitPriceNet = unitPriceFinal / (1 + (taxRate / 100));
    const subtotal = quantity * unitPriceNet;
    const taxAmount = subtotal * (taxRate / 100);
    const total = subtotal + taxAmount;

    this.itemForm.get('subtotal')?.setValue(subtotal, { emitEvent: false });
    this.itemForm.get('taxAmount')?.setValue(taxAmount, { emitEvent: false });
    this.itemForm.get('total')?.setValue(total, { emitEvent: false });
    this.itemForm.get('lineTaxes')?.setValue([], { emitEvent: false });
  }

  onSubmit(): void {
    if (this.itemForm.invalid) {
      console.error('Form is invalid');
      return;
    }

    this.loading = true;
    
    const formValue = this.itemForm.getRawValue();
    
    // Simulate API call
    setTimeout(() => {
      this.dialogRef.close({
        success: true,
        item: formValue,
        index: this.data.index
      });
      this.loading = false;
    }, 500);
  }

  onCancel(): void {
    this.dialogRef.close({ success: false });
  }
}
